import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield, Heart, Brain,
  Download, Star, CheckCircle, ArrowRight,
  MessageCircle, Calendar, BarChart3,
  Apple, Play, BookOpen,
  TrendingUp, Smile, Target, Zap, Search
} from 'lucide-react';

const LandingPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Feature categories for the comprehensive section
  const featureCategories = [
    {
      category: 'AI Companion',
      icon: Brain,
      color: 'from-cyclara-500 to-cyclara-600',
      description: 'Meet Cyra, your empathetic AI friend',
      features: [
        { title: 'Personal AI Chat', desc: 'Talk to Cyra anytime about your cycle, moods, or just to vent' },
        { title: 'Learns About YOU', desc: 'The more you use Cyclara, the more Cyra knows your patterns' },
        { title: '5 Personality Styles', desc: 'Choose: Supportive 💜, Informative 📚, Cheerful ✨, Calm 🧘, or Friendly 🤗' },
        { title: 'Works Offline', desc: 'No internet? No problem! Cyra lives on your phone' },
      ]
    },
    {
      category: 'Cycle Tracking',
      icon: Calendar,
      color: 'from-lavender-500 to-lavender-600',
      description: 'Complete period & cycle management',
      features: [
        { title: 'Beautiful Cycle Wheel', desc: 'See exactly where you are with a stunning visual display' },
        { title: '4 Phase Tracking', desc: 'Know your phase: Menstrual 🌙, Follicular 🌱, Ovulation 🌸, Luteal 🍂' },
        { title: 'Smart Predictions', desc: 'Predictions based on YOUR unique patterns' },
        { title: 'Period History', desc: 'Access all your past periods in one beautiful timeline' },
      ]
    },
    {
      category: 'Mood & Symptoms',
      icon: Smile,
      color: 'from-pink-500 to-rose-500',
      description: 'Track how you feel every day',
      features: [
        { title: '10 Mood Types', desc: 'From Great 😄 to Anxious 😰 — capture exactly how you feel' },
        { title: '13+ Symptoms', desc: 'Track cramps, headaches, bloating, fatigue, cravings, and more' },
        { title: 'Intensity Levels', desc: 'Rate how strongly you feel on a 1-5 scale' },
        { title: 'Pattern Detection', desc: 'Discover which moods link to your cycle phases' },
      ]
    },
    {
      category: 'Smart Insights',
      icon: TrendingUp,
      color: 'from-amber-500 to-orange-500',
      description: 'AI-powered analytics & patterns',
      features: [
        { title: 'Mood Patterns', desc: 'See your most common moods and how they relate to your cycle' },
        { title: 'Similar Days AI', desc: 'AI finds days in your past that felt like today — with what helped!' },
        { title: 'Top Symptoms Chart', desc: 'Identify your most frequent symptoms with visual charts' },
        { title: 'Proactive Tips', desc: 'Get advice based on where you are in your cycle' },
      ]
    },
  ];

  // Quick features for the grid
  const quickFeatures = [
    { icon: Brain, title: 'AI Companion', description: 'Chat with Cyra anytime — she learns your patterns and remembers what helps you.', color: 'from-cyclara-500 to-cyclara-600' },
    { icon: Calendar, title: 'Cycle Tracking', description: 'Beautiful cycle wheel, 4 phase tracking, and smart period predictions.', color: 'from-lavender-500 to-lavender-600' },
    { icon: Heart, title: 'Mood & Symptoms', description: '10 mood types, 13+ symptoms, intensity tracking, and pattern detection.', color: 'from-pink-500 to-rose-500' },
    { icon: TrendingUp, title: 'Smart Insights', description: 'AI finds similar days in your history and suggests what helped before.', color: 'from-amber-500 to-orange-500' },
    { icon: BookOpen, title: 'Private Journal', description: 'Write freely — entries are cycle-linked and AI-searchable for advice.', color: 'from-green-500 to-emerald-500' },
    { icon: Target, title: 'Fertility Tracking', description: 'Optional fertile window and ovulation predictions when you need them.', color: 'from-blue-500 to-cyan-500' },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Download the App',
      description: 'Get Cyclara free from the App Store or Google Play. No sign-up required.',
      icon: Download,
    },
    {
      step: '02',
      title: 'Log Your Cycle',
      description: 'Start tracking your period and symptoms with our beautiful, intuitive interface.',
      icon: Calendar,
    },
    {
      step: '03',
      title: 'Chat with Cyra',
      description: 'Talk to your AI companion whenever you need support, advice, or just someone to listen.',
      icon: MessageCircle,
    },
    {
      step: '04',
      title: 'Discover Insights',
      description: 'Understand your patterns with personalized insights and cycle predictions.',
      icon: BarChart3,
    },
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      text: 'Finally an app that doesn\'t sell my data! Cyra actually understands what I\'m going through.',
      rating: 5,
      avatar: '👩‍🦰',
    },
    {
      name: 'Emily R.',
      text: 'The offline feature is amazing. I can track everything without worrying about my privacy.',
      rating: 5,
      avatar: '👩‍🦱',
    },
    {
      name: 'Jessica L.',
      text: 'Love how the AI adapts to my cycle phase. It\'s like having a supportive friend in my pocket.',
      rating: 5,
      avatar: '👩',
    },
  ];

  const faqs = [
    {
      q: 'Is Cyclara really free?',
      a: 'Yes! Cyclara is completely free with no hidden costs, subscriptions, or in-app purchases.',
    },
    {
      q: 'How does the AI work without internet?',
      a: 'We use a small but powerful AI model that runs entirely on your device. No data is ever sent to any server.',
    },
    {
      q: 'Can I export my data?',
      a: 'Absolutely! You can export all your data as a JSON file anytime from the Settings screen.',
    },
    {
      q: 'What happens if I delete the app?',
      a: 'All your data is stored locally, so it will be deleted with the app. We recommend exporting a backup first.',
    },
  ];

  return (
    <main className="relative">
      {/* ==================== HERO SECTION ==================== */}
      <section className="min-h-screen flex items-center pt-32 pb-24 px-6 md:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Badge */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 
                         border border-green-200 text-green-700 text-sm font-medium mb-8"
              >
                <Shield className="w-4 h-4" />
                <span>100% Private • Your Data Never Leaves Your Device</span>
              </motion.div>

              {/* Headline */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-midnight-500 leading-tight mb-6">
                Your Private
                <span className="block gradient-text">Period Companion</span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl text-midnight-400 mb-10 leading-relaxed max-w-lg">
                Meet Cyclara — an AI-powered wellness companion that helps you track your cycle,
                understand your mood, and get empathetic support. <strong>All 100% Free.</strong>
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <motion.a
                  href="#download"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary flex items-center justify-center gap-3"
                >
                  <Download className="w-5 h-5" />
                  <span>Download Free</span>
                </motion.a>
                <motion.a
                  href="#features"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary flex items-center justify-center gap-3"
                >
                  <span>See Features</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.a>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-8 text-sm text-midnight-400">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {['👩‍🦰', '👩‍🦱', '👩', '👩‍🦳'].map((emoji, i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-cyclara-100 flex items-center justify-center 
                                            border-2 border-white text-sm">
                        {emoji}
                      </div>
                    ))}
                  </div>
                  <span>10K+ happy users</span>
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="ml-1">4.9/5</span>
                </div>
              </div>
            </motion.div>

            {/* Right: Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative flex justify-center"
            >
              {/* Decorative circles */}
              <div className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyclara-200/50 to-lavender-200/50 blur-3xl" />

              {/* Phone mockup with real image */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10"
              >
                <img
                  src="/images/showcase-home.png"
                  alt="Cyclara App Home Screen"
                  className="w-[280px] h-auto rounded-[2.5rem] shadow-2xl shadow-cyclara-500/30"
                />
              </motion.div>

              {/* Floating elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-8 -right-8 w-20 h-20 rounded-2xl bg-white shadow-xl
                         flex items-center justify-center"
              >
                <span className="text-4xl">🔒</span>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -bottom-4 -left-8 w-16 h-16 rounded-2xl bg-white shadow-xl
                         flex items-center justify-center"
              >
                <span className="text-3xl">💜</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES SECTION ==================== */}
      <section id="features" className="section bg-white/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyclara-100 text-cyclara-600
                          text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-6">
              Everything You Need,
              <span className="gradient-text"> Nothing You Don't</span>
            </h2>
            <p className="text-xl text-midnight-400 max-w-2xl mx-auto">
              Cyclara is designed with your privacy and wellbeing as the top priorities.
            </p>
          </motion.div>

          {/* Quick Features Grid */}
          <motion.div
            variants={stagger}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
          >
            {quickFeatures.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                className="feature-card group"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color}
                              flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-midnight-500 mb-3">{feature.title}</h3>
                <p className="text-midnight-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Detailed Feature Categories */}
          <div className="space-y-8">
            {featureCategories.map((cat, catIndex) => (
              <motion.div
                key={cat.category}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: catIndex * 0.1 }}
                className="bg-white rounded-3xl shadow-xl shadow-midnight-500/5 border border-cyclara-100 overflow-hidden"
              >
                <div className={`bg-gradient-to-r ${cat.color} p-6 text-white`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                      <cat.icon className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{cat.category}</h3>
                      <p className="text-white/80">{cat.description}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {cat.features.map((feature, i) => (
                      <div key={i} className="p-4 rounded-2xl bg-slate-50 hover:bg-cyclara-50 transition-colors">
                        <h4 className="font-semibold text-midnight-500 mb-2">{feature.title}</h4>
                        <p className="text-sm text-midnight-400">{feature.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* More Features Banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-cyclara-50 to-lavender-50 rounded-3xl p-8 border border-cyclara-100"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyclara-500 to-lavender-500 flex items-center justify-center">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-midnight-500">Plus So Much More</h3>
                  <p className="text-midnight-400">PDF health reports, calendar view, smart notifications, themes & more!</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  <CheckCircle className="w-4 h-4" />
                  100% FREE
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyclara-100 text-cyclara-700 rounded-full text-sm font-medium">
                  <Shield className="w-4 h-4" />
                  No Ads
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== APP SHOWCASE GALLERY ==================== */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-white via-cyclara-50/30 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-cyclara-100 to-lavender-100
                          text-cyclara-600 text-sm font-medium mb-4">
              See It In Action
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-6">
              Beautiful Design, <span className="gradient-text">Powerful Features</span>
            </h2>
            <p className="text-xl text-midnight-400 max-w-3xl mx-auto">
              Experience a period tracker that's as beautiful as it is intelligent.
              Every screen designed with care, every feature built with purpose.
            </p>
          </motion.div>

          {/* Showcase Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                image: '/images/showcase-home.png',
                title: 'Beautiful Phase Cards',
                subtitle: 'With Real Insights',
                description: "Know what's happening in your body at every stage.",
                color: 'from-pink-500 to-rose-500'
              },
              {
                image: '/images/showcase-cyra.png',
                title: 'Meet Cyra',
                subtitle: 'Your On-Device Private AI',
                description: '100% offline. Judgment-free. Always here to support you.',
                color: 'from-cyclara-500 to-lavender-500'
              },
              {
                image: '/images/showcase-insights.png',
                title: 'Deep Insights',
                subtitle: 'That Support Your Wellness',
                description: 'Mood patterns, similar days, and personalized guidance.',
                color: 'from-purple-500 to-indigo-500'
              },
              {
                image: '/images/showcase-calendar.png',
                title: 'A Clear Calendar',
                subtitle: 'Built for Your Cycle',
                description: 'See period days, fertile windows, predictions, and entries.',
                color: 'from-blue-500 to-cyan-500'
              },
              {
                image: '/images/showcase-cycle.png',
                title: 'Understand Every Phase',
                subtitle: 'Of Your Cycle',
                description: 'Science-based explanations tailored to you.',
                color: 'from-amber-500 to-orange-500'
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`group relative ${index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              >
                <div className="relative bg-white rounded-3xl shadow-xl shadow-midnight-500/10 overflow-hidden
                            border border-cyclara-100 hover:shadow-2xl hover:shadow-cyclara-500/20
                            transition-all duration-500 hover:-translate-y-2">
                  {/* Image Container */}
                  <div className={`relative h-[420px] bg-gradient-to-br ${item.color} overflow-hidden`}>
                    {/* Decorative elements */}
                    <div className="absolute inset-0 opacity-30">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
                    </div>

                    {/* Phone Frame */}
                    <div className="absolute inset-0 flex items-center justify-center p-2">
                      <div className="relative h-full py-3 transform group-hover:scale-105 transition-transform duration-500">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="h-full w-auto object-contain rounded-[2rem] shadow-2xl"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-midnight-500">
                      {item.title} <span className="text-cyclara-500">{item.subtitle}</span>
                    </h3>
                    <p className="text-midnight-400 mt-2">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="text-midnight-400 mb-6">Ready to experience it yourself?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#download" className="btn-primary inline-flex items-center gap-2">
                <Download className="w-5 h-5" />
                Download Free
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== AI SECTION ==================== */}
      <section className="section bg-gradient-to-br from-midnight-500 via-midnight-600 to-lavender-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyclara-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-lavender-500 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-cyclara-300
                            text-sm font-medium mb-6">
                🤖 AI-Powered
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Meet <span className="text-cyclara-400">Cyra</span>, Your AI Companion
              </h2>
              <p className="text-xl text-white/70 mb-8 leading-relaxed">
                Cyra isn't just a chatbot — she learns about YOU. She remembers your patterns,
                knows what helps you feel better, and gives advice based on where you are in your cycle.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { text: 'Remembers your name and preferences', icon: '💜' },
                  { text: 'Knows your cycle phase and typical symptoms', icon: '📅' },
                  { text: 'Finds similar days in your history with what helped', icon: '🔍' },
                  { text: 'Runs 100% on your phone — completely private', icon: '🔒' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-white/90">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/how-ai-works"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20
                         rounded-xl text-white font-medium transition-colors"
              >
                <Brain className="w-5 h-5" />
                <span>Learn How the AI Works</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Chat mockup */}
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20">
                <div className="space-y-4">
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="bg-cyclara-500 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                      <p className="text-white text-sm">I'm feeling really anxious today 😔</p>
                    </div>
                  </div>

                  {/* Cyra response */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyclara-400 to-lavender-400 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">🌸</span>
                    </div>
                    <div className="bg-white/20 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
                      <p className="text-white text-sm leading-relaxed">
                        I hear you, Sarah. 💜 You're on day 23 — just a few days before your period when anxiety often peaks.
                        Last month around this time, you mentioned that a warm bath helped. Maybe worth trying?
                      </p>
                    </div>
                  </div>

                  {/* Typing indicator */}
                  <div className="flex items-center gap-2 text-white/50 text-sm">
                    <motion.div
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="flex gap-1"
                    >
                      <span className="w-2 h-2 bg-white/50 rounded-full" />
                      <span className="w-2 h-2 bg-white/50 rounded-full" />
                      <span className="w-2 h-2 bg-white/50 rounded-full" />
                    </motion.div>
                    <span>Cyra is typing...</span>
                  </div>
                </div>
              </div>

              {/* Floating stats */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4"
              >
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-cyclara-500" />
                  <div>
                    <p className="text-xs text-midnight-400">Similar Day Found</p>
                    <p className="font-bold text-midnight-500">Dec 22 — Bath helped!</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== AI EDUCATION CALL-OUT ==================== */}
      <section className="py-16 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyclara-100 via-lavender-100 to-cyclara-100" />
        <div className="absolute inset-0 opacity-50">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyclara-300 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-lavender-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto relative z-10"
        >
          <div className="bg-white rounded-3xl shadow-2xl shadow-cyclara-500/20 p-8 md:p-12 border border-cyclara-200 text-center">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className="inline-block mb-6"
            >
              <span className="text-6xl">🤔</span>
            </motion.div>

            <h2 className="text-3xl md:text-4xl font-bold text-midnight-500 mb-4">
              Curious How AI Actually Works?
            </h2>
            <p className="text-lg text-midnight-400 mb-8 max-w-2xl mx-auto">
              No tech jargon, no confusing terms. We explain how Cyra understands you and keeps
              your data private — in plain English that anyone can understand!
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 text-sm text-midnight-400">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>5-minute read</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-midnight-400">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>No tech background needed</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-midnight-400">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Fun visual examples</span>
              </div>
            </div>

            <Link
              to="/how-ai-works"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyclara-500 to-lavender-500
                       text-white font-semibold rounded-2xl shadow-lg shadow-cyclara-500/30
                       hover:shadow-xl hover:shadow-cyclara-500/40 hover:scale-105 transition-all duration-300"
            >
              <Brain className="w-6 h-6" />
              <span>Discover How Cyra's AI Works</span>
              <ArrowRight className="w-5 h-5" />
            </Link>

            <p className="mt-6 text-sm text-midnight-400">
              Spoiler: It's like having a really good friend who keeps a diary about you! 📔
            </p>
          </div>
        </motion.div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section id="how-it-works" className="section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-lavender-100 text-lavender-600 
                          text-sm font-medium mb-4">
              How It Works
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-6">
              Simple as <span className="gradient-text">1-2-3-4</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                {/* Connector line */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 
                                bg-gradient-to-r from-cyclara-300 to-transparent z-0" />
                )}
                
                <div className="relative z-10 text-center">
                  <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-cyclara-100 to-lavender-100 
                                flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <item.icon className="w-10 h-10 text-cyclara-500" />
                  </div>
                  <span className="text-5xl font-bold text-cyclara-200">{item.step}</span>
                  <h3 className="text-xl font-bold text-midnight-500 mt-2 mb-3">{item.title}</h3>
                  <p className="text-midnight-400">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRIVACY SECTION ==================== */}
      <section id="privacy" className="section bg-midnight-500 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyclara-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-lavender-500 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-cyclara-300 
                            text-sm font-medium mb-6">
                Privacy First
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Your Data <span className="text-cyclara-400">Never</span> Leaves Your Device
              </h2>
              <p className="text-xl text-white/70 mb-8 leading-relaxed">
                Unlike other apps, Cyclara doesn't use cloud storage, doesn't require accounts, 
                and never sends your sensitive health data anywhere. Everything stays on your phone.
              </p>

              <div className="space-y-4">
                {[
                  'No accounts or sign-ups required',
                  'AI runs 100% on your device',
                  'No internet connection needed',
                  'Export or delete data anytime',
                  'Zero tracking or analytics',
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Privacy illustration */}
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                <div className="text-center mb-8">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-500 
                                flex items-center justify-center mb-4">
                    <Shield className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">Your Private Space</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: '📱', label: 'On-Device Storage' },
                    { icon: '🤖', label: 'Local AI' },
                    { icon: '🔐', label: 'Encrypted Data' },
                    { icon: '🚫', label: 'No Cloud' },
                  ].map((item, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-4 text-center">
                      <span className="text-3xl mb-2 block">{item.icon}</span>
                      <span className="text-sm text-white/70">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className="section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyclara-100 text-cyclara-600 
                          text-sm font-medium mb-4">
              Testimonials
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-6">
              Loved by <span className="gradient-text">Thousands</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-white rounded-3xl p-8 shadow-xl shadow-midnight-500/5 border border-cyclara-100"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-midnight-500 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-cyclara-100 flex items-center justify-center text-2xl">
                    {testimonial.avatar}
                  </div>
                  <span className="font-semibold text-midnight-500">{testimonial.name}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section id="faq" className="section bg-cyclara-50/50">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-lavender-100 text-lavender-600 
                          text-sm font-medium mb-4">
              FAQ
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-midnight-500">
              Got Questions?
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl border border-cyclara-100 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer 
                                  font-semibold text-midnight-500 hover:text-cyclara-500 transition-colors">
                  {faq.q}
                  <span className="ml-4 text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-6 pb-6 text-midnight-400">
                  {faq.a}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== DOWNLOAD CTA ==================== */}
      <section id="download" className="section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative bg-gradient-to-br from-cyclara-500 via-cyclara-600 to-lavender-600 
                     rounded-[3rem] p-12 md:p-16 text-center text-white overflow-hidden"
          >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-white rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white rounded-full blur-3xl" />
            </div>

            <div className="relative z-10">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="text-6xl mb-6"
              >
                🌸
              </motion.div>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Take Control?
              </h2>
              <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
                Download Cyclara today and experience period tracking the way it should be — 
                private, supportive, and entirely yours.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <motion.a
                  href="https://apps.apple.com/app/id6757277141"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'click', {
                        event_category: 'Download',
                        event_label: 'App Store Download Button',
                        value: 1
                      });
                    }
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-3 px-8 py-4 bg-white text-midnight-500
                           font-semibold rounded-2xl shadow-xl hover:shadow-2xl transition-all"
                >
                  <Apple className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-xs opacity-70">Download on the</div>
                    <div className="text-lg font-bold -mt-1">App Store</div>
                  </div>
                </motion.a>

                <div
                  className="flex items-center gap-3 px-8 py-4 bg-white/50 text-midnight-300
                           font-semibold rounded-2xl shadow-lg cursor-not-allowed opacity-60"
                >
                  <Play className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-xs opacity-70">Coming Soon to</div>
                    <div className="text-lg font-bold -mt-1">Google Play</div>
                  </div>
                </div>
              </div>

              <p className="mt-8 text-white/60 text-sm">
                100% Free • No Ads • No In-App Purchases
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
